import { Point } from "../models/Point";
import { EncodingUtils } from "../utils/EncodingUtils";
import { SvgUtils } from "../utils/SvgUtils";
import { VisualizerBase } from "./VisualizerBase";

export class Ethereum extends VisualizerBase {
    constructor(_placeholderMode: boolean = false) {
        super("ethereum", _placeholderMode);
    }

    public override generate(hex: string, coreMode: boolean = false): SVGSVGElement {
        const svg = this.createSvg();
        this.hexArray = EncodingUtils.hexStringToHexArray(hex);

        const size = 320;
        const center = size / 2;
        const triangleLength = 75;
        const halfTriangleLength = 37.5;
        const sr3 = Math.sqrt(3);
        const sr3HTL = sr3 * halfTriangleLength;

        const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");

        if(!coreMode) {
            const idOB = `circleOutbounding_${this.chainIdName}`;
            const dOB = SvgUtils.getDOfPathOfCircle(0, 0, 150);
            const obCircle = document.createElementNS("http://www.w3.org/2000/svg", "path");
            obCircle.id = idOB;
            obCircle.setAttribute("d", `${dOB}`);
            defs.appendChild(obCircle);
        }

        const points: Array<Point> = [ new Point(0, 0),
            new Point(-halfTriangleLength, -sr3HTL),
            new Point(0, -sr3HTL * 2),
            new Point(halfTriangleLength, -sr3HTL)
        ];
        const polygonId = `polygon_60deg_75_${this.chainIdName}`;
        const polygon = SvgUtils.createPolygonPath(polygonId, points, true, ``, "", 0);
        defs.appendChild(polygon);

        svg.appendChild(defs);
        
        if(!coreMode) {
            // outbound: moving 3 circles
            this.appendMovingCirclesOutbound(svg, 150);
            // outbound: scaling circle
            this.appendDynamicScalingOutbound(svg, 1.4);
        }

        const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
        // 0: rotate
        const angle = this.placeholderMode ? 0 : parseInt(this.hexArray[0], 16);

        // polygon-1
        const polygon1 = document.createElementNS("http://www.w3.org/2000/svg", "use");
        polygon1.setAttribute("href", `#${polygonId}`);
        polygon1.setAttribute("fill", this.getColorHex(1, 2, 3));
        polygon1.setAttribute("transform", `rotate(0,0,0)`);
        group.appendChild(polygon1);
        // polygon-2
        const polygon2 = document.createElementNS("http://www.w3.org/2000/svg", "use");
        polygon2.setAttribute("href", `#${polygonId}`);
        polygon2.setAttribute("fill", this.getColorHex(4, 5, 6));
        polygon2.setAttribute("transform", `rotate(60,0,0)`);
        group.appendChild(polygon2);
        // polygon-3
        const polygon3 = document.createElementNS("http://www.w3.org/2000/svg", "use");
        polygon3.setAttribute("href", `#${polygonId}`);
        polygon3.setAttribute("fill", this.getColorHex(7, 8, 9));
        polygon3.setAttribute("transform", `rotate(120,0,0)`);
        group.appendChild(polygon3);
        // polygon-4
        const polygon4 = document.createElementNS("http://www.w3.org/2000/svg", "use");
        polygon4.setAttribute("href", `#${polygonId}`);
        polygon4.setAttribute("fill", this.getColorHex(10, 11, 12));
        polygon4.setAttribute("transform", `rotate(180,0,0)`);
        group.appendChild(polygon4);
        // polygon-5
        const polygon5 = document.createElementNS("http://www.w3.org/2000/svg", "use");
        polygon5.setAttribute("href", `#${polygonId}`);
        polygon5.setAttribute("fill", this.getColorHex(13, 14, 15));
        polygon5.setAttribute("transform", `rotate(240,0,0)`);
        group.appendChild(polygon5);
        // polygon-6
        const polygon6 = document.createElementNS("http://www.w3.org/2000/svg", "use");
        polygon6.setAttribute("href", `#${polygonId}`);
        polygon6.setAttribute("fill", this.getColorHex(16, 17, 18));
        polygon6.setAttribute("transform", `rotate(300,0,0)`);
        group.appendChild(polygon6);
        
        group.setAttribute("transform", `rotate(${angle},${center},${center}) translate(${center} ${center})`);
        svg.appendChild(group);
        return svg;
    }
    
}

class SixDots {
    static renderSvg(hex: string): SVGSVGElement {
        const chainIdName = "";
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svg.setAttribute("version", "1.1");
        svg.setAttribute('fill', 'none');
        svg.setAttribute('viewBox', '0 0 320 320');

        const hexArray = EncodingUtils.hexStringToHexArray(hex);
        const size = 320;
        const gap = 10, gap2 = 20, gapHalf = 5;
        const center = size / 2;
        const sr3 = Math.sqrt(3);
        const r = 32; //(sr3 * width - 4 * gap - gap2 * sr3) / (2 * sr3 + 8);
        const R = center - (r + gap * 2);// (2 * width - 20 - gap2) / (4+sr3);
        const quarterR = R / 4;

        const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");

        const dOB = SvgUtils.getDOfPathOfCircle(0, 0, 150);
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "path");
        circle.id = `circleOutbounding_${chainIdName}`;
        circle.setAttribute("d", `${dOB}`);
        defs.appendChild(circle);

        svg.appendChild(defs);

        const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
        // 0: rotate
        const angle = parseInt(hexArray[0], 16);
        // 1-1: (center, center - R)
        const x1 = center;
        const y1 = center - R;
        const c11 = SvgUtils.drawCircle(x1, y1, r, `#${hexArray[1]}${hexArray[2]}${hexArray[3]}`, "", 0, true, 0.3, 5);
        group.appendChild(c11);
        // 2-1
        const x21 = x1 - sr3 * quarterR;//x1 - sr3 * R / 4
        const y2 = center - quarterR;// y1 + R * 3 / 4;
        const c21 = SvgUtils.drawCircle(x21, y2, r, `#${hexArray[4]}${hexArray[5]}${hexArray[6]}`, "", 0, true, 1, 6);
        group.appendChild(c21);
        // 2-2
        const x22 = x1 + sr3 * quarterR;//x1 + sr3 * R / 4
        const c22 = SvgUtils.drawCircle(x22, y2, r, `#${hexArray[7]}${hexArray[8]}${hexArray[9]}`, "", 0, true, 2, 7);
        group.appendChild(c22);
        // 3-1
        const x31 = center - (R * sr3 / 2);
        const y3 = center + R / 2;
        const c31 = SvgUtils.drawCircle(x31, y3, r, `#${hexArray[10]}${hexArray[11]}${hexArray[12]}`, "", 0, true, 3, 9);
        group.appendChild(c31);
        // 3-2
        const c32 = SvgUtils.drawCircle(center, y3, r, `#${hexArray[13]}${hexArray[14]}${hexArray[15]}`, "", 0, true, 2.5, 8);
        group.appendChild(c32);
        // 3-3
        const x33 = center + (R * sr3 / 2);
        const c33 = SvgUtils.drawCircle(x33, y3, r, `#${hexArray[16]}${hexArray[17]}${hexArray[18]}`, "", 0, true, 1.5, 6);
        group.appendChild(c33);
        
        // outbound circle
        const ob3 = SvgUtils.drawAnimateOutbounding3("ethereum", center, center, `#${hexArray[17]}${hexArray[18]}${hexArray[19]}49`, 1.5);
        svg.appendChild(ob3);

        group.setAttributeNS(null, "transform", `translate(${center} ${center}) rotate(${angle}) translate(${-center} ${-center})`);
        svg.appendChild(group);

        return svg;
    }
    
}